import { useEffect, useRef, type RefObject } from "react";

/** below this viewport width the list is not one screen tall (phones scroll the page), so it cannot fit */
const FIT_MIN_VIEWPORT = 768;

/**
 * "Auto" page size of a server-paged list: as many rows as the list's scroll box shows without scrolling.
 *
 * The page size follows the box size and the row height, never the data: a new page size loads a new page,
 * during which no rows are rendered, so a size computed from "whatever is rendered now" would flip between
 * the fallback and the measured row height and reload the list in an endless loop (2026-10-20: hundreds of
 * list requests per second and a flickering list). So the row height is measured from real rows, kept,
 * and only measured again when the layout changes (`layoutKey`: visible columns, cards vs. table) or the
 * box is resized. The page size is recomputed on those events only.
 */
export function useAutoPageSize({
  enabled,
  boxRef,
  rowSelector,
  fallbackRowHeight,
  reserved,
  perPage,
  setPerPage,
  hasRows,
  layoutKey,
  min = 5,
  max = 100,
}: {
  enabled: boolean;
  boxRef: RefObject<HTMLElement>;
  /** a data row inside the box (its height is the step) */
  rowSelector: string;
  /** used when the box is resized before any row of the layout was rendered */
  fallbackRowHeight: number;
  /** pixels of the box that are not rows (sticky column header, a day header) */
  reserved: number;
  perPage: number;
  setPerPage: (perPage: number) => void;
  hasRows: boolean;
  /** changes whenever the row height may change for the same data (visible columns, cards vs. table) */
  layoutKey?: string;
  min?: number;
  max?: number;
}) {
  const perPageRef = useRef(perPage);
  perPageRef.current = perPage;
  /** measured row height; null until rows of the current layout were seen */
  const rowHeightRef = useRef<number | null>(null);

  const measureRef = useRef<() => boolean>(() => false);
  measureRef.current = () => {
    const box = boxRef.current;
    const rows = box ? [...box.querySelectorAll<HTMLElement>(rowSelector)] : [];
    const heights = rows.map((row) => row.getBoundingClientRect().height).filter((h) => h > 0);
    if (!heights.length) return false;
    // the median: a single taller row (a wrapped name) does not decide the page size
    heights.sort((a, b) => a - b);
    rowHeightRef.current = heights[Math.floor(heights.length / 2)];
    return true;
  };

  const computeRef = useRef<() => void>(() => undefined);
  computeRef.current = () => {
    const box = boxRef.current;
    if (!enabled || !box || window.innerWidth < FIT_MIN_VIEWPORT) return;
    const rowHeight = rowHeightRef.current ?? fallbackRowHeight;
    const fits = Math.floor((box.clientHeight - reserved) / rowHeight);
    const next = Math.min(max, Math.max(min, fits));
    if (next !== perPageRef.current) setPerPage(next);
  };

  // a new layout: forget the row height, it is measured again from the next rendered rows
  useEffect(() => {
    rowHeightRef.current = null;
  }, [layoutKey]);

  // the first rows of a layout: replace the fallback height by the measured one (once per layout)
  useEffect(() => {
    if (!enabled || !hasRows || rowHeightRef.current !== null) return;
    if (measureRef.current()) computeRef.current();
  }, [enabled, hasRows, layoutKey]);

  // box size changes (window resize, sidebar, the detail above it): re-measure if rows are shown, recompute.
  // Re-attached on a layout change, the box can be a different element then (cards vs. table).
  useEffect(() => {
    const box = boxRef.current;
    if (!enabled || !box) return;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let lastHeight = box.clientHeight;
    // without a measured row height the page size stays as it is until the first rows arrive
    if (rowHeightRef.current !== null) computeRef.current();
    const observer = new ResizeObserver(() => {
      if (box.clientHeight === lastHeight) return;
      lastHeight = box.clientHeight;
      clearTimeout(timer);
      timer = setTimeout(() => {
        measureRef.current();
        computeRef.current();
      }, 200);
    });
    observer.observe(box);
    return () => {
      clearTimeout(timer);
      observer.disconnect();
    };
  }, [enabled, boxRef, layoutKey, reserved, min, max]);
}
